import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import moment from 'moment';
import { makeStyles, useTheme } from '@mui/styles';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  useMediaQuery
} from '@mui/material';

//import { getInitials } from 'helpers';
import { useHistory } from 'react-router-dom';
import { setOrderDetail } from 'store/actions/restaurant';
import { useSelector, useDispatch } from 'react-redux';
import { transformOrderId } from 'util/orderUtils'

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: 0
  },
  inner: {
    minWidth: 1050
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  textLink: {
		textDecoration: 'underline',
	},
  // --- Compact (phone / folded foldable / small tablet) card list ---
  cardListHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5, 0, 1.5),
    flexWrap: 'wrap'
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5)
  },
  listCard: {
    padding: theme.spacing(1.5)
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  cardTitle: {
    minWidth: 0,
    flex: 1
  },
  cardTotal: {
    fontWeight: 700,
    whiteSpace: 'nowrap'
  },
  cardChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1)
  },
  cardFields: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    columnGap: theme.spacing(1.5),
    rowGap: theme.spacing(0.5),
    marginTop: theme.spacing(1)
  },
  cardLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap'
  },
  cardValue: {
    fontSize: '0.85rem',
    overflowWrap: 'anywhere'
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  pagination: {
    width: '100%',
    '& .MuiTablePagination-toolbar': {
      flexWrap: 'wrap',
      justifyContent: 'flex-end'
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiTablePagination-toolbar': {
        paddingLeft: theme.spacing(1)
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: '0.75rem',
        margin: 0
      },
      '& .MuiTablePagination-actions': {
        marginLeft: theme.spacing(1)
      }
    }
  }
}));

const OrdersTable = props => {
  const { className, orders, ...rest } = props;

  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();

  // Below `md` a 10 column table cannot be read without horizontal scrolling,
  // so phones, folded foldables and portrait tablets get a stacked card list.
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);

  const showOrderDetail = (order) => dispatch(setOrderDetail(order));
  const user = useSelector((state) => state.account.user);
  const currencySymbol = (user && user.currencySymbol) ? user.currencySymbol : "$";


  const handleSelectAll = event => {
    const { orders } = props;

    let selectedRestaurants;

    if (event.target.checked) {
      selectedRestaurants = (orders || []).map(order => order.id);
    } else {
      selectedRestaurants = [];
    }

    setSelectedOrders(selectedRestaurants);
  };

  const handleSelectOne = (event, urlIdentifier) => {
    const selectedIndex = selectedOrders.indexOf(urlIdentifier);
    let newSelectedOrders = [];

    if (selectedIndex === -1) {
      newSelectedOrders = newSelectedOrders.concat(selectedOrders, urlIdentifier);
    } else if (selectedIndex === 0) {
      newSelectedOrders = newSelectedOrders.concat(selectedOrders.slice(1));
    } else if (selectedIndex === selectedOrders.length - 1) {
      newSelectedOrders = newSelectedOrders.concat(selectedOrders.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedOrders = newSelectedOrders.concat(
        selectedOrders.slice(0, selectedIndex),
        selectedOrders.slice(selectedIndex + 1)
      );
    }

    setSelectedOrders(newSelectedOrders);
    props.selectorders(newSelectedOrders);
  };

  const handlePageChange = (event, page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
  };

  const getOrderTotal = (order) => {
    // Handle online orders (order.order.items)
    if (order?.order?.items) {
      const total = order.order.items.filter((item) => item.dish?.isDeletedDish !== true).reduce((count, curItem) => {
        return count + curItem.quantity * curItem.subTotal;
      }, 0);
      return total.toFixed(2).toString();
    }
    // Handle local orders (order.dishes)
    if (order?.dishes) {
      const total = order.dishes.reduce((count, curItem) => {
        const itemPrice = curItem.subTotal !== undefined ? curItem.subTotal : curItem.price;
        return count + curItem.quantity * itemPrice;
      }, 0);
      return total.toFixed(2).toString();
    }
    return '0.00';
  };

  const orderCreateTime = (order) => {
    if (order && order.createdAt) {
      if (order.createdAt && typeof order.createdAt === 'object' && '_seconds' in order.createdAt) {
        return  moment(new Date(order.createdAt._seconds * 1000)).format('DD/MM/YYYY HH:mm:ss')
      }
      return moment(order.createdAt).format('DD/MM/YYYY HH:mm:ss')
    }
    return 'N/A';
  };

  const openOrderDetail = (e, order) => {
    showOrderDetail(order);
    history.push('/orderDetail');
  };

  const isOrderRefunded = (order) => {
    if (order && order.payment && order.payment.amount_refunded) {
      return 'YES';
    }
    if (order && order.payment && order.payment.result_code === 'FULL_REFUND'){
       return 'YES';
    }
    if (order && order.tillRefunds && order.tillRefunds.length > 0){
      return 'YES';
   }

   if (order && order.adyenRefunds && order.adyenRefunds.length > 0){
    return 'YES';
 }

 if (order && order.chinaPaymentsRefunds && order.chinaPaymentsRefunds.length > 0){
  return 'YES';
}

    if (order.isRefunded) {
      if (order.isRefunded === true) {
        return 'YES';
      } else {
          for (var dish in order.dishes) {
             if (dish.isRefunded === true) {
               return 'YES';
             }
          }
          return 'NO';
      }
      
    } else {
      return 'NO';
    }
  }

  const getDeliveryPartner = (order) => {
    if (!order) return '';
    const raw = order.deliveryPartner ?? order.order?.deliveryPartner;
    if (raw === null || raw === undefined || raw === '') return '';
    if (typeof raw === 'object') {
      const label = raw.name ?? raw.label ?? raw.type;
      return label != null && label !== '' ? String(label) : '';
    }
    return String(raw);
  };

  const paymentStatus = (order) => {
    if (order && order.payment && order.payment.status) {
      return order.payment.status;
    }
    if (order && order.payment && order.payment.result_code) {
      return order.payment.result_code;
    }
    if (order && order.payment && order.payment.eventCode) {
      return order.payment.eventCode;
    }

    if (order && order.payment && order.payment.orderStatus) {
      return order.payment.orderStatus;
    }

    return 'N/A';
  }

  const orderList = orders || [];
  // One slice, shared by both layouts, so page state survives a resize.
  const pagedOrders = orderList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const allSelected = orderList.length > 0 && selectedOrders.length === orderList.length;

  const CardField = ({ label, value }) => (
    <>
      <Typography component="dt" className={classes.cardLabel}>{label}</Typography>
      <Typography component="dd" className={classes.cardValue}>{value || '—'}</Typography>
    </>
  );

  CardField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.node
  };

  const renderCards = () => (
    <>
      <div className={classes.cardListHeader}>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              color="primary"
              indeterminate={selectedOrders.length > 0 && !allSelected}
              onChange={handleSelectAll}
              size="small"
            />
          }
          label={<Typography variant="body2">Select all</Typography>}
        />
        <Typography variant="body2" color="textSecondary">
          {orderList.length} order{orderList.length === 1 ? '' : 's'}
        </Typography>
      </div>
      <div className={classes.cardList}>
        {pagedOrders.length === 0 ? (
          <Typography className={classes.emptyState} color="textSecondary">
            No orders to show.
          </Typography>
        ) : (
          pagedOrders.map(order => (
            <Paper
              key={order.id}
              variant="outlined"
              className={classes.listCard}
              data-testid="order-card"
            >
              <div className={classes.cardHeader}>
                <Checkbox
                  checked={selectedOrders.indexOf(order.id) !== -1}
                  color="primary"
                  onChange={event => handleSelectOne(event, order.id)}
                  size="small"
                  inputProps={{ 'aria-label': `select order ${transformOrderId(order.id)}` }}
                />
                <div className={classes.cardTitle}>
                  <Button size="small" onClick={e => openOrderDetail(e, order)}>
                    <span className={classes.textLink}>{transformOrderId(order.id)}</span>
                  </Button>
                </div>
                <Typography className={classes.cardTotal}>
                  {currencySymbol}{getOrderTotal(order)}
                </Typography>
              </div>

              <div className={classes.cardChips}>
                {order.status ? <Chip size="small" label={order.status} /> : null}
                <Chip size="small" variant="outlined" label={paymentStatus(order)} />
                {isOrderRefunded(order) === 'YES' ? (
                  <Chip size="small" color="warning" label="Refunded" />
                ) : null}
              </div>

              <dl className={classes.cardFields}>
                <CardField label="Created" value={orderCreateTime(order)} />
                <CardField
                  label="Customer"
                  value={(order.user && order.user.name) || order.customerName || ''}
                />
                <CardField
                  label="Table"
                  value={(order.restaurant && order.restaurant.tableId) || order.tableNumber || order.tableNo || ''}
                />
                <CardField label="Delivery" value={getDeliveryPartner(order)} />
              </dl>
            </Paper>
          ))
        )}
      </div>
    </>
  );

  return (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <CardContent className={classes.content}>
        {isCompact ? renderCards() : (
          <TableContainer component={Paper}>
            <Table className={classes.inner}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      color="primary"
                      indeterminate={
                        selectedOrders.length > 0 &&
                        selectedOrders.length < orderList.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Order Number</TableCell>
                  <TableCell>Created Time</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Table</TableCell>
                  <TableCell>Delivery partner</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Status</TableCell>
                  <TableCell>Refunded</TableCell>
                  <TableCell>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedOrders.map(order => (
                  <TableRow
                    className={classes.tableRow}
                    hover
                    key={order.id}
                    selected={selectedOrders.indexOf(order.id) !== -1}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedOrders.indexOf(order.id) !== -1}
                        color="primary"
                        onChange={event => handleSelectOne(event, order.id)}
                        value="true"
                      />
                    </TableCell>
                    <TableCell>
                     
                      <Button onClick={e => openOrderDetail(e, order)}>
                        <div className={classes.textLink}>
                          <Typography variant="body1">{transformOrderId(order.id)}</Typography>
                        </div>
                        </Button>
                    
                    </TableCell>
                    <TableCell>{orderCreateTime(order)}</TableCell>
                    <TableCell>
                      {(order.user && order.user.name) || order.customerName || ''}
                    </TableCell>
                    <TableCell>
                      {(order.restaurant && order.restaurant.tableId) || order.tableNumber || order.tableNo || ''}
                    </TableCell>
                    <TableCell>{getDeliveryPartner(order)}</TableCell>
                    <TableCell>{order.status}</TableCell>
                    <TableCell>{paymentStatus(order)}</TableCell>
                    <TableCell>{isOrderRefunded(order)}</TableCell>
                    <TableCell>
                       {currencySymbol}{getOrderTotal(order)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
        )}
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          className={classes.pagination}
          component="div"
          count={orderList.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[50, 100]}
          labelRowsPerPage={isCompact ? 'Rows' : 'Rows per page:'}
        />
      </CardActions>
    </Card>
  );
};

OrdersTable.propTypes = {
  className: PropTypes.string,
  orders: PropTypes.array,
  selectorders: PropTypes.func,
};

export default OrdersTable;
